import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { Edit, Trash2, X, Save, Check, User, DollarSign, Users } from "lucide-react";

const apiUrl = process.env.REACT_APP_API_URL;

const ManageAccountBudgetTable = ({ accessToken, setError, setRefresh, refresh, setToast }) => {
  const [accountBudgetData, setAccountBudgetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [currentStep, setCurrentStep] = useState(1); // 1: select account, 2: set budget, 3: select users
  const [formData, setFormData] = useState({
    accountId: "",
    accountName: "",
    accountNo: "",
    budget: "",
    users: ""
  });
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
const filteredUsers = usersList.filter(user => 
  user.toLowerCase().includes(searchQuery.toLowerCase())
);
  // Fetch account budget data and available accounts
  useEffect(() => {
    fetchAccountBudgetData();
    fetchUsers();
  }, [accessToken, refresh]);
  const fetchUsers = async () => {
    try {
      const query = `
        query {
          getAllUsersForBudgetAlert {
            emails
            message
            success
          }
        }
      `;
      
      const response = await axios.post(
        apiUrl,
        { query },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      
      if (response.data.errors && response.data.errors[0].errorType === "Unauthorized") {
        setError("Unauthorized");
      } else if (
        response.data.data && 
        response.data.data.getAllUsersForBudgetAlert && 
        response.data.data.getAllUsersForBudgetAlert.success &&
        response.data.data.getAllUsersForBudgetAlert.emails
      ) {
        setUsersList(response.data.data.getAllUsersForBudgetAlert.emails);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setToast({
        type: "error",
        message: "Failed to fetch users",
      });
    }
  }
  const fetchAccountBudgetData = async () => {
    setLoading(true);
    try {
      const query = `
        query {
          getBudgetAccountTable {
            id
            AccountName
            AccountNo
            Budget
            Users
          }
        }
      `;
      
      const response = await axios.post(
        apiUrl,
        { query },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      
      if (response.data.errors && response.data.errors[0].errorType === "Unauthorized") {
        setError("Unauthorized");
      } else if (response.data.data && response.data.data.getBudgetAccountTable) {
        setAccountBudgetData(response.data.data.getBudgetAccountTable);
      }
    } catch (error) {
      console.error("Error fetching account budget data:", error);
      setToast({
        type: "error",
        message: "Failed to fetch account budget data",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAccounts = async () => {
    try {
      const query = `
        query {
          getAWSAccounts {
            id
            AccountName
            AccountNo
          }
        }
      `;
      
      const response = await axios.post(
        apiUrl,
        { query },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      
      if (response.data.errors && response.data.errors[0].errorType === "Unauthorized") {
        setError("Unauthorized");
      } else if (response.data.data && response.data.data.getAWSAccounts) {
        const allAccounts = response.data.data.getAWSAccounts;
        
        // Use the current state directly instead of a local variable
        const filteredAccounts = allAccounts.filter(account => 
          !accountBudgetData.some(budget => budget.AccountNo === account.AccountNo)
        );
        
        setAvailableAccounts(filteredAccounts);
      }
    } catch (error) {
      console.error("Error fetching available accounts:", error);
      setToast({
        type: "error",
        message: "Failed to fetch available accounts",
      });
    }
  };
  const handleAddAccount = async () => {
    setIsSubmitting(true); 
    try {
      const query = `
        mutation AddAccountBudget($AccountName: String!, $AccountNo: String!, $Budget: Float!, $Users: [String]) {
          addAccountBudgetDetails(
            AccountName: $AccountName,
            AccountNo: $AccountNo,
            Budget: $Budget,
            Users: $Users
          )
        }
      `;
      
      const variables = {
        AccountName: formData.accountName,
        AccountNo: formData.accountNo,
        Budget: parseFloat(formData.budget),
        Users: formData.users,
      };
      
      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      
      if (response.data.errors) {
        throw new Error(response.data.errors[0]?.message || "Failed to add account budget");
      }
      
      setToast({
        type: "success",
        message: "Account budget added successfully",
      });
      
      resetForm();
      setRefresh(!refresh);
    } catch (error) {
      console.error("Error adding account budget:", error);
      setToast({
        type: "error",
        message: "Failed to add account budget",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateBudget = async (id, newBudget) => {
    try {
      const query = `
        mutation UpdateBudget($id: ID!, $Budget: Float!) {
          updateBudgetForAccount(id: $id, Budget: $Budget)
        }
      `;
      
      const variables = {
        id,
        Budget: parseFloat(newBudget),
      };
      
      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      
      if (response.data.errors) {
        throw new Error(response.data.errors[0]?.message || "Failed to update budget");
      }
      
      setToast({
        type: "success",
        message: "Budget updated successfully",
      });
      
      setIsEditing(false);
      setEditingId(null);
      setRefresh(!refresh);
    } catch (error) {
      console.error("Error updating budget:", error);
      setToast({
        type: "error",
        message: "Failed to update budget",
      });
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      // This is a placeholder for the delete mutation
      // You'll need to replace this with the actual mutation when available
      const query = `
        mutation DeleteAccountBudget($id: ID!) {
          deleteAccountBudget(id: $id)
        }
      `;
      
      const variables = {
        id: accountToDelete.id,
      };
      
      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      
      if (response.data.errors) {
        throw new Error(response.data.errors[0]?.message || "Failed to delete account budget");
      }
      
      setToast({
        type: "success",
        message: "Account budget deleted successfully",
      });
      
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
      setRefresh(!refresh);
    } catch (error) {
      console.error("Error deleting account budget:", error);
      setToast({
        type: "error",
        message:  "Failed to delete account budget",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAccountSelect = (account) => {
    setFormData({
      ...formData,
      accountId: account.id,
      accountName: account.AccountName,
      accountNo: account.AccountNo,
    });
    setCurrentStep(2);
  };

  const handleBudgetInput = (e) => {
    const value = e.target.value;
    if (!isNaN(value) && (value === "" || parseFloat(value) >= 0)) {
      setFormData({
        ...formData,
        budget: value,
      });
    }
  };

  const handleUserToggle = (user) => {
    setFormData((prev) => {
      const updatedUsers = prev.users.includes(user)
        ? prev.users.filter((u) => u !== user)
        : [...prev.users, user];
      
      return {
        ...prev,
        users: updatedUsers,
      };
    });
  };

  const resetForm = () => {
    setFormData({
      accountId: "",
      accountName: "",
      accountNo: "",
      budget: "",
      users: [],
    });
    setCurrentStep(1);
    setIsAdding(false);
  };

  // Function to edit users for an account
  const handleEditUsers = (account) => {
    setFormData({
      accountId: account.id,
      accountName: account.AccountName,
      accountNo: account.AccountNo,
      budget: account.Budget.toString(),
      users: account.Users || [],
    });
    setUsersDialogOpen(true);
  };

  const handleUpdateUsers = async () => {
    try {
      // This is a placeholder for the update users mutation
      // You'll need to replace this with the actual mutation when available
      const query = `
        mutation updateBudgetAlertUsers($id: ID!, $Users: [String]!) {
          updateBudgetAlertUsers(id: $id, Users: $Users)
        }
      `;
      
      const variables = {
        id: formData.accountId,
        Users: formData.users,
      };
      
      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      
      if (response.data.errors) {
        throw new Error(response.data.errors[0]?.message || "Failed to update users");
      }
      
      setToast({
        type: "success",
        message: "Users updated successfully",
      });
      
      setUsersDialogOpen(false);
      resetForm();
      setRefresh(!refresh);
    } catch (error) {
      console.error("Error updating users:", error);
      setToast({
        type: "error",
        message: "Failed to update users",
      });
    }
  };

  // Format currency for display
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const displayData = showAll ? accountBudgetData : accountBudgetData.slice(0, 4);

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 mt-6">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-[#003366]">Account Budgets</h3>
          <button
            onClick={() => {
              setIsAdding(true);
              fetchAvailableAccounts();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add New Account Budget
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <CircularProgress style={{ color: "#003366" }} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#003366]/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Account Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Account No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Budget
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Users
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayData.map((account) => (
                  <tr key={account.id} className="hover:bg-[#003366]/5">
                    <td className="px-6 py-4 whitespace-nowrap text-[#003366]">
                      {account.AccountName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {account.AccountNo}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {isEditing && editingId === account.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            min="0"
                            className="px-2 py-1 border rounded w-24"
                            value={formData.budget}
                            onChange={handleBudgetInput}
                          />
                          <button
                            onClick={() => handleUpdateBudget(account.id, formData.budget)}
                            className="text-[#003366] hover:text-green-800"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => {
                              setIsEditing(false);
                              setEditingId(null);
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          {formatCurrency(account.Budget)}
                          <button
                            onClick={() => {
                              setIsEditing(true);
                              setEditingId(account.id);
                              setFormData({
                                ...formData,
                                budget: account.Budget.toString(),
                              });
                            }}
                            className="text-[#003366] hover:text-[#004488]"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {account.Users && account.Users.length > 0 ? (
                        <div className="flex items-center">
                          <span className="bg-[#003366]/10 text-[#003366] text-xs font-medium px-2 py-1 rounded-full">
                            {account.Users.length} users
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">No users</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center space-x-3">
                      <button
                        onClick={() => handleEditUsers(account)}
                        className="text-[#003366] hover:text-[#004488] p-1 rounded-full"
                        title="Edit Users"
                      >
                        <Users className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setAccountToDelete(account);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-600 hover:text-red-800 p-1 rounded-full"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {accountBudgetData.length > 4 && (
              <div className="text-center mt-4">
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-[#003366] hover:text-[#004488] font-medium"
                >
                  {showAll ? "Show Less" : "Show More"}
                </button>
              </div>
            )}
            {accountBudgetData.length === 0 && !isAdding && (
              <div className="text-center py-12">
                <div className="text-[#003366]">
                  <svg
                    className="w-12 h-12 mx-auto mb-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 12H4"
                    />
                  </svg>
                  <p className="text-lg font-semibold">No Data Available</p>
                  <p className="mt-2">
                    Click the Add New button to add your first account budget
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Account Budget Dialog */}
      {isAdding && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="w-full max-w-md bg-white rounded-lg shadow-xl overflow-hidden">
            <div className="flex justify-between items-center p-6 bg-[#003366]/5 border-b">
              <h2 className="text-xl font-semibold text-[#003366]">
                {currentStep === 1 && "Select Account"}
                {currentStep === 2 && "Set Budget"}
                {currentStep === 3 && "Select Users for Alerts"}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {currentStep === 1 && (
                <div>
                  <h3 className="text-lg font-medium text-[#003366] mb-3">Available Accounts</h3>
                  {availableAccounts.length === 0 ? (
                    <p className="text-gray-600">No available accounts to add.</p>
                  ) : (
                    <div className="max-h-64 overflow-y-auto border rounded-md">
                      {availableAccounts.map((account) => (
                        <div
                          key={account.id}
                          className="flex items-center justify-between p-3 hover:bg-[#003366]/5 cursor-pointer border-b"
                          onClick={() => handleAccountSelect(account)}
                        >
                          <div>
                            <p className="font-medium text-[#003366]">{account.AccountName}</p>
                            <p className="text-sm text-gray-600">{account.AccountNo}</p>
                          </div>
                          <div className="text-[#003366]">
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {currentStep === 2 && (
                <div>
                  <div className="mb-4">
                    <p className="text-gray-600">
                      Setting budget for <span className="font-medium text-[#003366]">{formData.accountName}</span>
                    </p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Budget Amount
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <DollarSign className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        min="0"
                        required
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003366] focus:ring-opacity-100 focus:border-transparent py-2 px-3 border"
                        placeholder="0.00"
                        value={formData.budget}
                        onChange={handleBudgetInput}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between mt-6">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="px-4 py-2 text-[#003366] hover:bg-[#003366]/10 rounded"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setCurrentStep(3)}
                      disabled={!formData.budget}
                      className={`px-4 py-2 rounded ${
                        formData.budget
                          ? "bg-[#003366] text-white hover:bg-[#004488]"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

            {currentStep === 3 && (
  <div>
    <div className="mb-4">
      <p className="text-gray-600">
        Select users to receive alerts for <span className="font-medium text-[#003366]">{formData.accountName}</span>
      </p>
    </div>
    
    {/* Search Box */}
    <div className="mb-3 relative">
      <input
        type="text"
        className="w-full border border-gray-300 rounded-md py-2 px-3 pl-9 focus:outline-none focus:ring-2 focus:ring-[#003366] focus:border-transparent"
        placeholder="Search users..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      {searchQuery && (
        <button 
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
          onClick={() => setSearchQuery("")}
        >
          <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
        </button>
      )}
    </div>
    
    {/* Selection controls */}
    <div className="flex justify-between items-center mb-2 text-sm">
      <span className="text-gray-500">
        {formData.users.length} of {usersList.length} selected
      </span>
      <div className="space-x-3">
        <button 
          type="button"
          className="text-[#003366] hover:underline text-sm font-medium"
          onClick={() => setFormData({...formData, users: filteredUsers.length > 0 ? [...new Set([...formData.users, ...filteredUsers])] : formData.users})}
        >
          Select all {searchQuery ? "filtered" : ""}
        </button>
        <button 
          type="button"
          className="text-[#003366] hover:underline text-sm font-medium"
          onClick={() => {
            if (searchQuery) {
              const newUsers = formData.users.filter(user => !filteredUsers.includes(user));
              setFormData({...formData, users: newUsers});
            } else {
              setFormData({...formData, users: []});
            }
          }}
        >
          Deselect all {searchQuery ? "filtered" : ""}
        </button>
      </div>
    </div>
    
    {/* Users list */}
    <div className="max-h-64 overflow-y-auto border rounded-md">
      {filteredUsers.length > 0 ? (
        filteredUsers.map((user, index) => (
          <div
            key={index}
            className={`flex items-center justify-between p-3 hover:bg-[#003366]/5 cursor-pointer border-b ${
              formData.users.includes(user) ? "bg-[#003366]/10" : ""
            }`}
            onClick={() => handleUserToggle(user)}
          >
            <div className="flex items-center">
              <User className="h-5 w-5 text-gray-400 mr-2" />
              <span>{user}</span>
            </div>
            {formData.users.includes(user) && (
              <Check className="h-5 w-5 text-[#003366]" />
            )}
          </div>
        ))
      ) : (
        <div className="p-4 text-center text-gray-500">
          No users matching your search
        </div>
      )}
    </div>
    <div className="flex justify-between mt-6">
      <button
        onClick={() => setCurrentStep(2)}
        className="px-4 py-2 text-[#003366] hover:bg-[#003366]/10 rounded"
      >
        Back
      </button>
      <button
        onClick={handleAddAccount}
        disabled={isSubmitting}
        className="px-4 py-2 bg-[#003366] text-white hover:bg-[#004488] rounded flex items-center justify-center"
      >
        {isSubmitting ? (
          <>
            <CircularProgress size={20} color="inherit" className="mr-2" />
            Confirming...
          </>
        ) : (
          "Confirm"
        )}
      </button>
    </div>
  </div>
)}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            borderRadius: "8px",
            padding: "8px",
            maxWidth: "450px",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          style={{
            padding: "16px 24px",
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#003366",
          }}
        >
          {"Confirm Deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            style={{
              color: "#4a4a4a",
              fontSize: "1rem",
              marginBottom: "8px",
            }}
          >
            Are you sure you want to delete the budget for{" "}
            <b>{accountToDelete?.AccountName}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px", gap: "12px" }}>
          <button
            onClick={() => setDeleteDialogOpen(false)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#FFD700",
              color: "#003366",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#FFE44D")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#FFD700")
            }
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={handleDeleteAccount}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#003366",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#004488")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#003366")
            }
            autoFocus
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Save size={16} />
            )}
            Confirm
          </button>
        </DialogActions>
      </Dialog>

      {/* Edit Users Dialog */}
      <Dialog
  open={usersDialogOpen}
  onClose={() => setUsersDialogOpen(false)}
  aria-labelledby="edit-users-dialog-title"
  maxWidth="sm"
  fullWidth
  PaperProps={{
    style: {
      borderRadius: "8px",
    },
  }}
>
  <DialogTitle
    id="edit-users-dialog-title"
    style={{
      padding: "16px 24px",
      fontSize: "1.25rem",
      fontWeight: 600,
      color: "#003366",
      backgroundColor: "#003366/5",
      borderBottom: "1px solid #e5e7eb",
    }}
  >
    Modify Alert Users for {formData.accountName}
  </DialogTitle>
  <DialogContent style={{ padding: "24px" }}>
    {/* Search Box */}
    <div className="mb-3 relative mt-2">
      <input
        type="text"
        className="w-full border border-gray-300 rounded-md py-2 px-3 pl-9 focus:outline-none focus:ring-2 focus:ring-[#003366] focus:border-transparent"
        placeholder="Search users..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      {searchQuery && (
        <button 
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
          onClick={() => setSearchQuery("")}
        >
          <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
        </button>
      )}
    </div>
    
    {/* Selection controls */}
    <div className="flex justify-between items-center mb-2 text-sm">
      <span className="text-gray-500">
        {formData.users.length} of {usersList.length} selected
      </span>
      <div className="space-x-3">
        <button 
          type="button"
          className="text-[#003366] hover:underline text-sm font-medium"
          onClick={() => setFormData({...formData, users: filteredUsers.length > 0 ? [...new Set([...formData.users, ...filteredUsers])] : formData.users})}
        >
          Select all {searchQuery ? "filtered" : ""}
        </button>
        <button 
          type="button"
          className="text-[#003366] hover:underline text-sm font-medium"
          onClick={() => {
            if (searchQuery) {
              const newUsers = formData.users.filter(user => !filteredUsers.includes(user));
              setFormData({...formData, users: newUsers});
            } else {
              setFormData({...formData, users: []});
            }
          }}
        >
          Deselect all {searchQuery ? "filtered" : ""}
        </button>
      </div>
    </div>
    
    {/* Users list */}
    <div className="max-h-64 overflow-y-auto border rounded-md">
      {filteredUsers.length > 0 ? (
        filteredUsers.map((user, index) => (
          <div
            key={index}
            className={`flex items-center justify-between p-3 hover:bg-[#003366]/5 cursor-pointer border-b ${
              formData.users.includes(user) ? "bg-[#003366]/10" : ""
            }`}
            onClick={() => handleUserToggle(user)}
          >
            <div className="flex items-center">
              <User className="h-5 w-5 text-gray-400 mr-2" />
              <span>{user}</span>
            </div>
            {formData.users.includes(user) && (
              <Check className="h-5 w-5 text-[#003366]" />
            )}
          </div>
        ))
      ) : (
        <div className="p-4 text-center text-gray-500">
          No users matching your search
        </div>
      )}
    </div>
  </DialogContent>
  <DialogActions style={{ padding: "16px 24px", gap: "12px" }}>
    <button
      onClick={() => {
        setUsersDialogOpen(false);
        setSearchQuery(""); // Clear search when closing dialog
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 16px",
        backgroundColor: "#FFD700",
        color: "#003366",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "0.875rem",
        fontWeight: 500,
        transition: "background-color 0.2s",
      }}
      onMouseOver={(e) =>
        (e.currentTarget.style.backgroundColor = "#FFE44D")
      }
      onMouseOut={(e) =>
        (e.currentTarget.style.backgroundColor = "#FFD700")
      }
    >
      <X size={16} />
      Cancel
    </button>
    <button
      onClick={() => {
        handleUpdateUsers();
        setSearchQuery(""); // Clear search after saving
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 16px",
        backgroundColor: "#003366",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "0.875rem",
        fontWeight: 500,
        transition: "background-color 0.2s",
      }}
      onMouseOver={(e) =>
        (e.currentTarget.style.backgroundColor = "#004488")
      }
      onMouseOut={(e) =>
        (e.currentTarget.style.backgroundColor = "#003366")
      }
      autoFocus
    >
      <Save size={16} />
      Save Changes
    </button>
  </DialogActions>
</Dialog>
    </div>
  );
};

export default ManageAccountBudgetTable;